import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AgentAutomationResourceDraftPayload,
  AgentDraftContext,
  AgentReportDataBinding,
  AgentReportDefinition,
  AgentReportResourceDraftPayload,
  AgentReportSection,
  AgentReportTestIntent,
  AgentResourceDraft,
  AgentScheduleConfig,
  AgentWorkspace,
} from '@servicesai/api-client';
import {
  EmptyState,
  GlassButton,
  GlassCard,
  GlassInput,
  GlassSelect,
  GlassTextarea,
  StatusPill,
  useI18n,
} from '@servicesai/ui';
import { admin } from '../../lib/api.js';
import { ContextFields, ResourceGuidance } from './AgentResourcePanels.js';

type ReportDraft = Extract<AgentResourceDraft, { kind: 'report' }>;
type ReportFilter = AgentReportDataBinding['filters'][number];
type ReportTableSection = Extract<AgentReportSection, { kind: 'table' }>;

const REPORT_AGGREGATIONS: AgentReportDataBinding['aggregation'][] = [
  'none',
  'count',
  'sum',
  'average',
  'minimum',
  'maximum',
  'latest',
  'p50',
  'p95',
  'p99',
];

const REPORT_FILTER_OPERATORS: ReportFilter['operator'][] = [
  'eq',
  'neq',
  'in',
  'not_in',
  'gt',
  'gte',
  'lt',
  'lte',
  'exists',
];

const REPORT_WINDOWS = [
  { value: '', en: 'All available data', ar: 'كل البيانات المتاحة' },
  { value: '1440', en: '24 hours', ar: '٢٤ ساعة' },
  { value: '10080', en: '7 days', ar: '٧ أيام' },
  { value: '43200', en: '30 days', ar: '٣٠ يوماً' },
] as const;

function filterValueText(value: ReportFilter['value']): string {
  if (Array.isArray(value)) return JSON.stringify(value);
  if (value === null) return 'null';
  return value === undefined ? '' : String(value);
}

function parseFilterValue(value: string): Exclude<ReportFilter['value'], undefined> {
  const trimmed = value.trim();
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (trimmed === 'null') return null;
  if (/^-?(?:0|[1-9]\d*)(?:\.\d+)?$/u.test(trimmed)) return Number(trimmed);
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (
        Array.isArray(parsed)
        && parsed.every((item) => typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean')
      ) {
        return parsed;
      }
    } catch {
      // Keep incomplete array input editable as text.
    }
  }
  return value;
}

const EMPTY_CONTEXT: AgentDraftContext = {
  objective: '',
  rationale: '',
  expectedOutcome: '',
  constraints: [],
  successCriteria: [],
  ownerId: null,
  noteRefs: [],
};

const EMPTY_PAYLOAD: AgentReportResourceDraftPayload = {
  name: '',
  goals: [],
};

function blankDefinition(): AgentReportDefinition {
  return {
    key: 'new-report',
    identity: {
      title: { en: 'New report', ar: 'تقرير جديد' },
      description: { en: '', ar: '' },
    },
    goal: {
      en: 'A governed operational report.',
      ar: 'تقرير تشغيلي خاضع للحوكمة.',
    },
    bindings: [],
    sections: [
      {
        kind: 'narrative',
        title: { en: 'Overview', ar: 'نظرة عامة' },
        content: {
          en: 'Configure data bindings and add report sections.',
          ar: 'قم بإعداد روابط البيانات وإضافة أقسام التقرير.',
        },
        bindingKeys: [],
      },
    ],
    presentation: {
      layout: 'document',
      theme: 'default',
      density: 'comfortable',
      showHeader: true,
      showGeneratedAt: true,
    },
    delivery: {
      enabled: false,
      channels: [],
      formats: ['html'],
      audience: { type: 'all', values: [] },
    },
  };
}

function lifecycleState(lifecycle: AgentResourceDraft['lifecycle']) {
  if (lifecycle === 'verified' || lifecycle === 'promoted') return 'published';
  if (lifecycle === 'mapped') return 'in_review';
  if (lifecycle === 'archived') return 'rejected';
  return 'draft';
}

function keyFromName(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replaceAll(/[^a-z0-9_-]+/gu, '-')
    .replaceAll(/^-+|-+$/gu, '')
    .slice(0, 100) || 'new-report';
}

function newSection(
  kind: AgentReportSection['kind'],
  firstBinding: AgentReportDataBinding | undefined,
): AgentReportSection {
  const title = { en: 'Section', ar: 'قسم' };
  if (kind === 'metric') {
    return {
      kind,
      bindingKey: firstBinding?.key ?? '',
      label: { en: 'Metric', ar: 'مؤشر' },
      format: 'number',
    };
  }
  if (kind === 'table') {
    return {
      kind,
      title,
      bindingKey: firstBinding?.key ?? '',
      columns: [{
        field: firstBinding?.field ?? 'status',
        label: { en: 'Value', ar: 'القيمة' },
        format: 'text',
      }],
    };
  }
  if (kind === 'callout') {
    return {
      kind,
      title,
      tone: 'info',
      content: { en: 'Important report context.', ar: 'سياق مهم للتقرير.' },
      bindingKeys: [],
    };
  }
  if (kind === 'cta') {
    return {
      kind,
      label: { en: 'Open console', ar: 'فتح المنصة' },
      href: 'https://console.100.agentic.ae/agents',
      bindingKeys: [],
    };
  }
  return {
    kind,
    title,
    content: { en: 'Narrative section.', ar: 'قسم سردي.' },
    bindingKeys: [],
  };
}

function ReportDefinitionBuilder({
  slug,
  definition,
  onChange,
}: {
  slug: string;
  definition: AgentReportDefinition;
  onChange: (definition: AgentReportDefinition) => void;
}) {
  const { lang } = useI18n();
  const text = (en: string, ar: string) => lang === 'ar' ? ar : en;
  const catalog = useQuery({
    queryKey: ['agent-reporting-catalog', slug],
    queryFn: () => admin().agentReportingCatalog(slug),
  });
  const sources = catalog.data?.sources ?? [];
  const aggregations = catalog.data?.aggregations ?? REPORT_AGGREGATIONS;
  const fieldsForSource = (source: AgentReportDataBinding['source'], fallback: string) => {
    const fields = sources.find((item) => item.key === source)?.fields ?? [];
    return fields.includes(fallback) ? fields : [fallback, ...fields];
  };
  const updateBinding = (index: number, next: AgentReportDataBinding) => {
    const bindings = [...definition.bindings];
    bindings[index] = next;
    onChange({ ...definition, bindings });
  };
  const updateSection = (index: number, next: AgentReportSection) => {
    const sections = [...definition.sections];
    sections[index] = next;
    onChange({ ...definition, sections });
  };
  return (
    <div className="space-y-4">
      <div className="grid gap-3 lg:grid-cols-2">
        <label className="text-xs font-bold text-ink-soft">
          {text('Report key', 'مفتاح التقرير')}
          <GlassInput value={definition.key} onChange={(event) => onChange({ ...definition, key: keyFromName(event.target.value) })} className="mt-1 font-mono" dir="ltr" />
        </label>
        <div className="grid grid-cols-3 gap-2">
          <label className="text-xs font-bold text-ink-soft">
            {text('Layout', 'التخطيط')}
            <GlassSelect value={definition.presentation.layout} onChange={(event) => onChange({ ...definition, presentation: { ...definition.presentation, layout: event.target.value as AgentReportDefinition['presentation']['layout'] } })} className="mt-1">
              {['document', 'dashboard', 'brief'].map((value) => <option key={value}>{value}</option>)}
            </GlassSelect>
          </label>
          <label className="text-xs font-bold text-ink-soft">
            {text('Theme', 'السمة')}
            <GlassSelect value={definition.presentation.theme} onChange={(event) => onChange({ ...definition, presentation: { ...definition.presentation, theme: event.target.value as AgentReportDefinition['presentation']['theme'] } })} className="mt-1">
              {['default', 'executive', 'operational', 'security', 'governance', 'critical'].map((value) => <option key={value}>{value}</option>)}
            </GlassSelect>
          </label>
          <label className="text-xs font-bold text-ink-soft">
            {text('Density', 'الكثافة')}
            <GlassSelect value={definition.presentation.density} onChange={(event) => onChange({ ...definition, presentation: { ...definition.presentation, density: event.target.value as AgentReportDefinition['presentation']['density'] } })} className="mt-1">
              {['comfortable', 'compact'].map((value) => <option key={value}>{value}</option>)}
            </GlassSelect>
          </label>
        </div>
        {(['en', 'ar'] as const).map((locale) => (
          <div key={locale} className="space-y-3 rounded-xl border border-aeblack-100 p-3" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            <div className="text-xs font-extrabold">{locale.toUpperCase()}</div>
            <label className="block text-xs font-bold text-ink-soft">
              {text('Title', 'العنوان')}
              <GlassInput value={definition.identity.title[locale]} onChange={(event) => onChange({ ...definition, identity: { ...definition.identity, title: { ...definition.identity.title, [locale]: event.target.value } } })} className="mt-1" />
            </label>
            <label className="block text-xs font-bold text-ink-soft">
              {text('Description', 'الوصف')}
              <GlassTextarea rows={2} value={definition.identity.description?.[locale] ?? ''} onChange={(event) => onChange({ ...definition, identity: { ...definition.identity, description: { en: definition.identity.description?.en ?? '', ar: definition.identity.description?.ar ?? '', [locale]: event.target.value } } })} className="mt-1" />
            </label>
            <label className="block text-xs font-bold text-ink-soft">
              {text('Report goal / summary', 'هدف التقرير / الملخص')}
              <GlassTextarea rows={3} value={definition.goal[locale]} onChange={(event) => onChange({ ...definition, goal: { ...definition.goal, [locale]: event.target.value } })} className="mt-1" />
            </label>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-aeblack-100 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h4 className="text-sm font-extrabold">{text('Safe data bindings', 'روابط بيانات آمنة')}</h4>
            <p className="mt-1 text-xs text-ink-faint">{text('Only approved metadata fields are available; raw SQL and citizen content are never accepted.', 'تتوفر حقول البيانات الوصفية المعتمدة فقط؛ لا يتم قبول SQL أو محتوى المتعاملين.')}</p>
          </div>
          <GlassButton size="sm" onClick={() => onChange({
            ...definition,
            bindings: [...definition.bindings, {
              key: `metric_${definition.bindings.length + 1}`,
              scope: 'tenant',
              source: 'agent_runs',
              field: 'status',
              aggregation: 'count',
              filters: [],
              limit: 100,
            }],
          })}>{text('Add binding', 'إضافة رابط')}</GlassButton>
        </div>
        {definition.bindings.length === 0 ? (
          <p className="mt-3 text-xs text-ink-faint">{text('Narrative-only reports do not require a binding.', 'التقارير السردية فقط لا تتطلب رابط بيانات.')}</p>
        ) : (
          <div className="mt-3 space-y-2">
            {definition.bindings.map((binding, index) => (
              <div key={`${binding.key}-${index}`} className="rounded-lg bg-aeblack-50 p-3">
                <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_1fr_1fr_auto]">
                  <label className="text-[10px] font-bold text-ink-soft">
                    {text('Key', 'المفتاح')}
                    <GlassInput value={binding.key} onChange={(event) => updateBinding(index, { ...binding, key: keyFromName(event.target.value).replaceAll('-', '_') })} className="mt-1 font-mono" dir="ltr" />
                  </label>
                  <label className="text-[10px] font-bold text-ink-soft">
                    {text('Source', 'المصدر')}
                    <GlassSelect value={binding.source} onChange={(event) => {
                      const source = event.target.value as AgentReportDataBinding['source'];
                      updateBinding(index, {
                        ...binding,
                        source,
                        field: sources.find((item) => item.key === source)?.fields[0] ?? 'status',
                        filters: [],
                      });
                    }} className="mt-1">
                      {sources.length > 0
                        ? sources.map((source) => <option key={source.key} value={source.key}>{source.label}</option>)
                        : <option value={binding.source}>{binding.source}</option>}
                    </GlassSelect>
                  </label>
                  <label className="text-[10px] font-bold text-ink-soft">
                    {text('Field', 'الحقل')}
                    <GlassSelect value={binding.field} onChange={(event) => updateBinding(index, { ...binding, field: event.target.value })} className="mt-1 font-mono" dir="ltr">
                      {fieldsForSource(binding.source, binding.field).map((field) => <option key={field}>{field}</option>)}
                    </GlassSelect>
                  </label>
                  <label className="text-[10px] font-bold text-ink-soft">
                    {text('Aggregation', 'التجميع')}
                    <GlassSelect value={binding.aggregation} onChange={(event) => updateBinding(index, { ...binding, aggregation: event.target.value as AgentReportDataBinding['aggregation'] })} className="mt-1">
                      {(aggregations.includes(binding.aggregation) ? aggregations : [binding.aggregation, ...aggregations]).map((value) => <option key={value}>{value}</option>)}
                    </GlassSelect>
                  </label>
                  <label className="text-[10px] font-bold text-ink-soft">
                    {text('Window', 'النافذة')}
                    <GlassSelect
                      value={String(binding.windowMinutes ?? '')}
                      onChange={(event) => updateBinding(index, {
                        ...binding,
                        ...(event.target.value ? { windowMinutes: Number(event.target.value) } : { windowMinutes: undefined }),
                      })}
                      className="mt-1"
                    >
                      {!REPORT_WINDOWS.some((window) => window.value === String(binding.windowMinutes ?? '')) && binding.windowMinutes && (
                        <option value={binding.windowMinutes}>{binding.windowMinutes} {text('minutes', 'دقيقة')}</option>
                      )}
                      {REPORT_WINDOWS.map((window) => <option key={window.value || 'all'} value={window.value}>{text(window.en, window.ar)}</option>)}
                    </GlassSelect>
                  </label>
                  <GlassButton
                    size="sm"
                    variant="danger"
                    className="self-end"
                    aria-label={text(`Remove binding ${binding.key}`, `إزالة الرابط ${binding.key}`)}
                    onClick={() => onChange({ ...definition, bindings: definition.bindings.filter((_, itemIndex) => itemIndex !== index) })}
                  >
                    ×
                  </GlassButton>
                </div>
                <div className="mt-3 border-t border-aeblack-100 pt-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-[10px] font-extrabold">{text('Filters', 'المرشحات')}</div>
                      <p className="text-[9px] text-ink-faint">{text('All filters are allowlisted and combined with AND.', 'جميع المرشحات مسموح بها وتُدمج باستخدام AND.')}</p>
                    </div>
                    <GlassButton
                      size="sm"
                      onClick={() => updateBinding(index, {
                        ...binding,
                        filters: [...binding.filters, {
                          field: fieldsForSource(binding.source, binding.field)[0] ?? binding.field,
                          operator: 'eq',
                          value: '',
                        }],
                      })}
                    >
                      {text('Add filter', 'إضافة مرشح')}
                    </GlassButton>
                  </div>
                  {binding.filters.length === 0 ? (
                    <p className="mt-2 text-[10px] text-ink-faint">{text('No filters', 'لا توجد مرشحات')}</p>
                  ) : (
                    <div className="mt-2 space-y-2">
                      {binding.filters.map((filter, filterIndex) => (
                        <div key={`${filter.field}-${filterIndex}`} className="grid gap-2 rounded-lg border border-aeblack-100 bg-white p-2 sm:grid-cols-[1fr_1fr_2fr_auto]">
                          <label className="text-[10px] font-bold">
                            {text('Filter field', 'حقل المرشح')}
                            <GlassSelect
                              value={filter.field}
                              onChange={(event) => updateBinding(index, {
                                ...binding,
                                filters: binding.filters.map((candidate, candidateIndex) => candidateIndex === filterIndex ? { ...candidate, field: event.target.value } : candidate),
                              })}
                              className="mt-1 font-mono"
                              dir="ltr"
                            >
                              {fieldsForSource(binding.source, filter.field).map((field) => <option key={field}>{field}</option>)}
                            </GlassSelect>
                          </label>
                          <label className="text-[10px] font-bold">
                            {text('Operator', 'المعامل')}
                            <GlassSelect
                              value={filter.operator}
                              onChange={(event) => {
                                const operator = event.target.value as ReportFilter['operator'];
                                const nextFilter: ReportFilter = { ...filter, operator };
                                if (operator === 'exists') delete nextFilter.value;
                                else if (nextFilter.value === undefined) nextFilter.value = '';
                                updateBinding(index, {
                                  ...binding,
                                  filters: binding.filters.map((candidate, candidateIndex) => candidateIndex === filterIndex ? nextFilter : candidate),
                                });
                              }}
                              className="mt-1"
                            >
                              {REPORT_FILTER_OPERATORS.map((operator) => <option key={operator}>{operator}</option>)}
                            </GlassSelect>
                          </label>
                          <label className="text-[10px] font-bold">
                            {text('Value', 'القيمة')}
                            <GlassInput
                              value={filterValueText(filter.value)}
                              disabled={filter.operator === 'exists'}
                              onChange={(event) => updateBinding(index, {
                                ...binding,
                                filters: binding.filters.map((candidate, candidateIndex) => candidateIndex === filterIndex ? { ...candidate, value: parseFilterValue(event.target.value) } : candidate),
                              })}
                              placeholder={text('Text, number, boolean, or JSON array', 'نص أو رقم أو قيمة منطقية أو مصفوفة JSON')}
                              className="mt-1"
                              dir="ltr"
                            />
                          </label>
                          <GlassButton
                            size="sm"
                            variant="danger"
                            className="self-end"
                            aria-label={text(`Remove filter ${filterIndex + 1}`, `إزالة المرشح ${filterIndex + 1}`)}
                            onClick={() => updateBinding(index, {
                              ...binding,
                              filters: binding.filters.filter((_, candidateIndex) => candidateIndex !== filterIndex),
                            })}
                          >
                            ×
                          </GlassButton>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-aeblack-100 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h4 className="text-sm font-extrabold">{text('Report sections', 'أقسام التقرير')}</h4>
            <p className="mt-1 text-xs text-ink-faint">{text('Compose metrics, narrative, tables, callouts, and links.', 'أنشئ مؤشرات وسرداً وجداول وتنبيهات وروابط.')}</p>
          </div>
          <div className="flex flex-wrap gap-1">
            {(['metric', 'narrative', 'table', 'callout', 'cta'] as const).map((kind) => (
              <GlassButton key={kind} size="sm" onClick={() => onChange({ ...definition, sections: [...definition.sections, newSection(kind, definition.bindings[0])] })}>+ {kind}</GlassButton>
            ))}
          </div>
        </div>
        <div className="mt-3 space-y-3">
          {definition.sections.map((section, index) => (
            <div key={`${section.kind}-${index}`} className="rounded-lg bg-aeblack-50 p-3">
              <div className="flex items-center justify-between gap-2">
                <StatusPill state="draft" label={section.kind} />
                <GlassButton
                  size="sm"
                  variant="danger"
                  disabled={definition.sections.length === 1}
                  aria-label={text(`Remove ${section.kind} section`, `إزالة قسم ${section.kind}`)}
                  onClick={() => onChange({ ...definition, sections: definition.sections.filter((_, sectionIndex) => sectionIndex !== index) })}
                >
                  ×
                </GlassButton>
              </div>
              {section.kind === 'metric' && (
                <div className="mt-2 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                  <label className="text-[10px] font-bold">{text('Binding', 'الرابط')}<GlassSelect value={section.bindingKey} onChange={(event) => updateSection(index, { ...section, bindingKey: event.target.value })} className="mt-1">{definition.bindings.map((binding) => <option key={binding.key}>{binding.key}</option>)}</GlassSelect></label>
                  <label className="text-[10px] font-bold">{text('Label EN', 'التسمية EN')}<GlassInput value={section.label.en} onChange={(event) => updateSection(index, { ...section, label: { ...section.label, en: event.target.value } })} className="mt-1" /></label>
                  <label className="text-[10px] font-bold">{text('Label AR', 'التسمية AR')}<GlassInput value={section.label.ar} onChange={(event) => updateSection(index, { ...section, label: { ...section.label, ar: event.target.value } })} className="mt-1" dir="rtl" /></label>
                  <label className="text-[10px] font-bold">{text('Format', 'التنسيق')}<GlassSelect value={section.format} onChange={(event) => updateSection(index, { ...section, format: event.target.value as typeof section.format })} className="mt-1">{['number', 'percentage', 'currency', 'duration', 'text'].map((format) => <option key={format}>{format}</option>)}</GlassSelect></label>
                </div>
              )}
              {(section.kind === 'narrative' || section.kind === 'callout') && (
                <div className="mt-2 grid gap-2 lg:grid-cols-2">
                  <label className="text-[10px] font-bold">{text('Content EN', 'المحتوى EN')}<GlassTextarea rows={3} value={section.content.en} onChange={(event) => updateSection(index, { ...section, content: { ...section.content, en: event.target.value } })} className="mt-1" /></label>
                  <label className="text-[10px] font-bold">{text('Content AR', 'المحتوى AR')}<GlassTextarea rows={3} value={section.content.ar} onChange={(event) => updateSection(index, { ...section, content: { ...section.content, ar: event.target.value } })} className="mt-1" dir="rtl" /></label>
                </div>
              )}
              {section.kind === 'table' && (
                <div className="mt-2 space-y-3">
                  <div className="flex flex-wrap items-end justify-between gap-2">
                    <label className="min-w-64 flex-1 text-[10px] font-bold">
                      {text('Binding', 'الرابط')}
                      <GlassSelect value={section.bindingKey} onChange={(event) => updateSection(index, { ...section, bindingKey: event.target.value })} className="mt-1">
                        {definition.bindings.map((binding) => <option key={binding.key}>{binding.key}</option>)}
                      </GlassSelect>
                    </label>
                    <GlassButton
                      size="sm"
                      onClick={() => {
                        const binding = definition.bindings.find((candidate) => candidate.key === section.bindingKey);
                        const field = binding ? fieldsForSource(binding.source, binding.field)[0] ?? binding.field : 'status';
                        const nextColumn: ReportTableSection['columns'][number] = {
                          field,
                          label: { en: `Column ${section.columns.length + 1}`, ar: `العمود ${section.columns.length + 1}` },
                          format: 'text',
                        };
                        updateSection(index, { ...section, columns: [...section.columns, nextColumn] });
                      }}
                    >
                      {text('Add column', 'إضافة عمود')}
                    </GlassButton>
                  </div>
                  <div className="space-y-2">
                    {section.columns.map((column, columnIndex) => {
                      const binding = definition.bindings.find((candidate) => candidate.key === section.bindingKey);
                      const fields = binding ? fieldsForSource(binding.source, column.field) : [column.field];
                      return (
                        <div key={`${column.field}-${columnIndex}`} className="grid gap-2 rounded-lg border border-aeblack-100 bg-white p-2 sm:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_1fr_auto]">
                          <label className="text-[10px] font-bold">
                            {text('Column field', 'حقل العمود')}
                            <GlassSelect
                              value={column.field}
                              onChange={(event) => updateSection(index, {
                                ...section,
                                columns: section.columns.map((candidate, candidateIndex) => candidateIndex === columnIndex ? { ...candidate, field: event.target.value } : candidate),
                              })}
                              className="mt-1 font-mono"
                              dir="ltr"
                            >
                              {fields.map((field) => <option key={field}>{field}</option>)}
                            </GlassSelect>
                          </label>
                          <label className="text-[10px] font-bold">
                            {text('Label EN', 'التسمية EN')}
                            <GlassInput
                              value={column.label.en}
                              onChange={(event) => updateSection(index, {
                                ...section,
                                columns: section.columns.map((candidate, candidateIndex) => candidateIndex === columnIndex ? { ...candidate, label: { ...candidate.label, en: event.target.value } } : candidate),
                              })}
                              className="mt-1"
                            />
                          </label>
                          <label className="text-[10px] font-bold">
                            {text('Label AR', 'التسمية AR')}
                            <GlassInput
                              value={column.label.ar}
                              onChange={(event) => updateSection(index, {
                                ...section,
                                columns: section.columns.map((candidate, candidateIndex) => candidateIndex === columnIndex ? { ...candidate, label: { ...candidate.label, ar: event.target.value } } : candidate),
                              })}
                              className="mt-1"
                              dir="rtl"
                            />
                          </label>
                          <label className="text-[10px] font-bold">
                            {text('Format', 'التنسيق')}
                            <GlassSelect
                              value={column.format}
                              onChange={(event) => updateSection(index, {
                                ...section,
                                columns: section.columns.map((candidate, candidateIndex) => candidateIndex === columnIndex ? { ...candidate, format: event.target.value as typeof column.format } : candidate),
                              })}
                              className="mt-1"
                            >
                              {['number', 'percentage', 'currency', 'date', 'datetime', 'text'].map((format) => <option key={format}>{format}</option>)}
                            </GlassSelect>
                          </label>
                          <GlassButton
                            size="sm"
                            variant="danger"
                            className="self-end"
                            disabled={section.columns.length === 1}
                            aria-label={text(`Remove column ${columnIndex + 1}`, `إزالة العمود ${columnIndex + 1}`)}
                            onClick={() => updateSection(index, {
                              ...section,
                              columns: section.columns.filter((_, candidateIndex) => candidateIndex !== columnIndex),
                            })}
                          >
                            ×
                          </GlassButton>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {section.kind === 'cta' && (
                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  <label className="text-[10px] font-bold">{text('Label EN', 'التسمية EN')}<GlassInput value={section.label.en} onChange={(event) => updateSection(index, { ...section, label: { ...section.label, en: event.target.value } })} className="mt-1" /></label>
                  <label className="text-[10px] font-bold">{text('Label AR', 'التسمية AR')}<GlassInput value={section.label.ar} onChange={(event) => updateSection(index, { ...section, label: { ...section.label, ar: event.target.value } })} className="mt-1" /></label>
                  <label className="text-[10px] font-bold">{text('HTTPS link', 'رابط HTTPS')}<GlassInput value={section.href} onChange={(event) => updateSection(index, { ...section, href: event.target.value })} className="mt-1" dir="ltr" /></label>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DeliveryScheduleForm({
  slug,
  workspace,
}: {
  slug: string;
  workspace: AgentWorkspace;
}) {
  const { lang } = useI18n();
  const text = (en: string, ar: string) => lang === 'ar' ? ar : en;
  const queryClient = useQueryClient();
  const reports = workspace.effectiveConfig.reports.filter((report) => report.id);
  const [reportId, setReportId] = useState(reports[0]?.id ?? '');
  const [name, setName] = useState('');
  const [cron, setCron] = useState('0 6 * * 1');
  const [timezone, setTimezone] = useState('Asia/Dubai');
  const [audienceType, setAudienceType] = useState<'all' | 'role' | 'custom'>('role');
  const [audienceValues, setAudienceValues] = useState('admin');
  const create = useMutation({
    mutationFn: () => {
      const report = reports.find((item) => item.id === reportId);
      if (!report?.id) throw new Error(text('Choose a published report.', 'اختر تقريراً منشوراً.'));
      const schedule: AgentScheduleConfig = {
        name: name.trim() || `${report.identity.title.en} delivery`,
        purpose: 'digest',
        enabled: true,
        cron,
        timezone,
        conditions: { lang },
        audience: {
          type: audienceType,
          values: audienceValues.split(/[\n,]/u).map((value) => value.trim()).filter(Boolean),
        },
        channel: 'email',
        templateKey: null,
        quietHours: { enabled: false, start: '22:00', end: '07:00' },
        cooldownMinutes: 0,
        retryPolicy: { maxAttempts: 3, backoffMinutes: 5 },
        reportDefinitionId: report.id,
      };
      const context: AgentDraftContext = {
        objective: `Deliver ${report.identity.title.en} by email on a governed schedule.`,
        rationale: 'Provide repeatable operational visibility to authorized staff.',
        expectedOutcome: 'Authorized recipients receive the approved report on schedule.',
        constraints: ['Published report definition only', 'Staff recipients only'],
        successCriteria: ['Schedule verifies successfully', 'Delivery is auditable'],
        ownerId: null,
        noteRefs: [],
      };
      const payload: AgentAutomationResourceDraftPayload = {
        name: schedule.name,
        trigger: `Cron ${cron} in ${timezone}`,
        action: `Email report ${report.key}`,
        outcome: 'Auditable report delivery',
        mapping: schedule,
      };
      return admin().createAgentResourceDraft(slug, { kind: 'automation', context, payload });
    },
    onSuccess: async () => {
      setName('');
      await queryClient.invalidateQueries({ queryKey: ['agent-workspace', slug] });
    },
  });
  if (reports.length === 0) return null;
  return (
    <GlassCard className="p-5">
      <div>
        <h3 className="text-base font-extrabold">{text('Schedule approved-report delivery', 'جدولة تسليم التقرير المعتمد')}</h3>
        <p className="mt-1 text-xs text-ink-faint">{text('Creates an independent automation draft for verification and promotion.', 'ينشئ مسودة أتمتة مستقلة للتحقق والترقية.')}</p>
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <label className="text-xs font-bold">{text('Report', 'التقرير')}<GlassSelect value={reportId} onChange={(event) => setReportId(event.target.value)} className="mt-1">{reports.map((report) => <option key={report.id} value={report.id}>{report.identity.title[lang]}</option>)}</GlassSelect></label>
        <label className="text-xs font-bold">{text('Schedule name', 'اسم الجدول')}<GlassInput value={name} onChange={(event) => setName(event.target.value)} className="mt-1" /></label>
        <label className="text-xs font-bold">{text('Cron', 'تعبير Cron')}<GlassInput value={cron} onChange={(event) => setCron(event.target.value)} className="mt-1 font-mono" dir="ltr" /></label>
        <label className="text-xs font-bold">{text('Timezone', 'المنطقة الزمنية')}<GlassInput value={timezone} onChange={(event) => setTimezone(event.target.value)} className="mt-1" dir="ltr" /></label>
        <label className="text-xs font-bold">{text('Staff audience', 'جمهور الموظفين')}<GlassSelect value={audienceType} onChange={(event) => setAudienceType(event.target.value as typeof audienceType)} className="mt-1"><option value="role">role</option><option value="custom">staff IDs</option><option value="all">all active staff</option></GlassSelect></label>
        <label className="text-xs font-bold md:col-span-2">{text('Roles or staff UUIDs', 'الأدوار أو معرفات الموظفين')}<GlassInput value={audienceValues} disabled={audienceType === 'all'} onChange={(event) => setAudienceValues(event.target.value)} className="mt-1" /></label>
      </div>
      {create.error && <div className="aegov-alert alert-error mt-3"><div className="alert-content">{(create.error as Error).message}</div></div>}
      <div className="mt-3 flex justify-end"><GlassButton variant="primary" disabled={create.isPending || !reportId} onClick={() => create.mutate()}>{text('Create schedule draft', 'إنشاء مسودة الجدول')}</GlassButton></div>
    </GlassCard>
  );
}

export function AgentReportingPanel({
  slug,
  workspace,
  selectedNoteIds,
}: {
  slug: string;
  workspace: AgentWorkspace;
  selectedNoteIds: string[];
}) {
  const { lang } = useI18n();
  const text = (en: string, ar: string) => lang === 'ar' ? ar : en;
  const queryClient = useQueryClient();
  const drafts = workspace.resourceDrafts.filter((draft): draft is ReportDraft => draft.kind === 'report');
  const selectedNotes = useMemo(
    () => workspace.notes
      .filter((note) => selectedNoteIds.includes(note.id) && !note.archived_at)
      .map((note) => ({ id: note.id, revision: note.revision })),
    [selectedNoteIds, workspace.notes],
  );
  const [editing, setEditing] = useState<ReportDraft | null>(null);
  const [payload, setPayload] = useState<AgentReportResourceDraftPayload>(EMPTY_PAYLOAD);
  const [context, setContext] = useState<AgentDraftContext>(EMPTY_CONTEXT);
  const [definition, setDefinition] = useState<AgentReportDefinition | null>(null);
  // Live-safe by default: a preview of invented rows is indistinguishable from
  // a real one, so previewing sample data has to be a deliberate choice.
  const [dataMode, setDataMode] = useState<'sample' | 'live_safe'>('live_safe');
  const [mobilePreview, setMobilePreview] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [testIntent, setTestIntent] = useState<AgentReportTestIntent | null>(null);
  const [sendConfirmed, setSendConfirmed] = useState(false);
  const [sentMessage, setSentMessage] = useState<string | null>(null);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['agent-workspace', slug] });
  };
  const save = useMutation({
    mutationFn: () => {
      if (!payload.name.trim()) throw new Error(text('A report draft name is required.', 'اسم مسودة التقرير مطلوب.'));
      const input = {
        kind: 'report' as const,
        context: { ...context, noteRefs: selectedNotes },
        payload: { ...payload, name: payload.name.trim(), definition: definition ?? undefined },
      };
      return editing
        ? admin().updateAgentResourceDraft(slug, editing.id, { ...input, expectedRevision: editing.revision })
        : admin().createAgentResourceDraft(slug, input);
    },
    onSuccess: async ({ resourceDraft }) => {
      await refresh();
      if (resourceDraft.kind === 'report') {
        setEditing(resourceDraft);
        setPayload(resourceDraft.payload);
        setContext(resourceDraft.context);
        setDefinition(resourceDraft.payload.definition ?? null);
      }
    },
  });
  const verify = useMutation({
    mutationFn: (draft: ReportDraft) => admin().verifyAgentResourceDraft(slug, draft.id, { expectedRevision: draft.revision }),
    onSuccess: refresh,
  });
  const promote = useMutation({
    mutationFn: (draft: ReportDraft) => {
      const configDraft = workspace.versions.find((version) => version.state === 'draft');
      return admin().promoteAgentResourceDraft(slug, draft.id, {
        expectedRevision: draft.revision,
        ...(configDraft ? { configVersionId: configDraft.id, expectedConfigLockVersion: configDraft.lock_version } : {}),
      });
    },
    onSuccess: refresh,
  });
  const archive = useMutation({
    mutationFn: (draft: ReportDraft) => admin().archiveAgentResourceDraft(slug, draft.id, draft.revision),
    onSuccess: refresh,
  });
  const preview = useMutation({
    mutationFn: () => {
      if (!definition) throw new Error(text('Add a report definition first.', 'أضف تعريف التقرير أولاً.'));
      return admin().previewAgentReport(slug, { definition, dataMode, lang });
    },
  });
  const prepareTestSend = useMutation({
    mutationFn: () => {
      if (!definition) throw new Error(text('Add a report definition first.', 'أضف تعريف التقرير أولاً.'));
      return admin().createAgentReportTestIntent(slug, {
        definition,
        dataMode,
        lang,
        recipient: recipient.trim(),
      });
    },
    onSuccess: ({ intent }) => {
      setTestIntent(intent);
      setSendConfirmed(false);
      setSentMessage(null);
    },
  });
  const confirmTestSend = useMutation({
    mutationFn: () => {
      if (!testIntent || !sendConfirmed) throw new Error(text('Explicit confirmation is required.', 'التأكيد الصريح مطلوب.'));
      return admin().confirmAgentReportTestSend(slug, testIntent.token);
    },
    onSuccess: ({ sent }) => {
      setSentMessage(`${sent.provider} · ${sent.providerMessageId}`);
      setTestIntent(null);
      setSendConfirmed(false);
    },
  });
  const beginEdit = (draft: ReportDraft) => {
    setEditing(draft);
    setPayload(draft.payload);
    setContext(draft.context);
    setDefinition(draft.payload.definition ?? null);
    preview.reset();
  };
  const busy = save.isPending || verify.isPending || promote.isPending || archive.isPending;
  const error = save.error
    ?? verify.error
    ?? promote.error
    ?? archive.error
    ?? preview.error
    ?? prepareTestSend.error
    ?? confirmTestSend.error;
  return (
    <div className="space-y-4">
      <GlassCard className="p-5" strong>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-extrabold">{text('Reporting workspace', 'مساحة عمل التقارير')}</h2>
            <p className="mt-1 max-w-3xl text-xs text-ink-faint">{text('Design bilingual reports, bind approved data, preview responsive email output, and schedule governed delivery.', 'صمّم تقارير ثنائية اللغة واربط البيانات المعتمدة وعاين البريد المتجاوب وجدول التسليم الخاضع للحوكمة.')}</p>
          </div>
          <StatusPill state="in_progress" label={`${drafts.length} ${text('report drafts', 'مسودات تقارير')}`} />
        </div>
      </GlassCard>
      {error && <div className="aegov-alert alert-error" role="alert"><div className="alert-content">{(error as Error).message}</div></div>}
      {workspace.effectiveConfig.reports.length > 0 && (
        <GlassCard className="p-5">
          <h3 className="text-base font-extrabold">{text('Published reports', 'التقارير المنشورة')}</h3>
          <div className="mt-3 grid gap-2 lg:grid-cols-2">
            {workspace.effectiveConfig.reports.map((report) => (
              <div key={report.id ?? report.key} className="rounded-lg border border-aeblack-100 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div><div className="font-bold">{report.identity.title[lang]}</div><code className="text-[10px] text-ink-faint">{report.key}</code></div>
                  <StatusPill state="published" label={`${report.sections.length} ${text('sections', 'أقسام')}`} />
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
      <div>
        <h3 className="mb-3 text-base font-extrabold">{text('Independent report drafts', 'مسودات تقارير مستقلة')}</h3>
        {drafts.length === 0 ? (
          <GlassCard><EmptyState title={text('No report drafts yet', 'لا توجد مسودات تقارير بعد')} hint={text('Save a report concept with only a name, then configure it progressively.', 'احفظ فكرة تقرير بالاسم فقط ثم قم بإعدادها تدريجياً.')} /></GlassCard>
        ) : (
          <div className="grid gap-3 xl:grid-cols-2">
            {drafts.map((draft) => (
              <GlassCard key={draft.id} className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div><div className="font-extrabold">{draft.payload.name}</div><p className="mt-1 text-xs text-ink-soft">{draft.payload.description || draft.context.objective || text('Concept only', 'فكرة فقط')}</p></div>
                  <StatusPill state={lifecycleState(draft.lifecycle)} label={draft.lifecycle} />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {draft.lifecycle !== 'promoted' && draft.lifecycle !== 'archived' && (
                    <>
                      <GlassButton size="sm" disabled={busy} onClick={() => beginEdit(draft)}>{text('Edit', 'تعديل')}</GlassButton>
                      <GlassButton size="sm" disabled={busy || !draft.payload.definition} onClick={() => verify.mutate(draft)}>{text('Verify', 'تحقق')}</GlassButton>
                      <GlassButton size="sm" variant="primary" disabled={busy || draft.lifecycle !== 'verified'} onClick={() => promote.mutate(draft)}>{text('Promote', 'ترقية')}</GlassButton>
                      <GlassButton size="sm" variant="danger" disabled={busy} onClick={() => archive.mutate(draft)}>{text('Archive', 'أرشفة')}</GlassButton>
                    </>
                  )}
                </div>
              </GlassCard>
            ))}
          </div>
        )}
      </div>
      <GlassCard className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-base font-extrabold">{editing ? text('Edit report draft', 'تعديل مسودة التقرير') : text('New report concept', 'فكرة تقرير جديدة')}</h3>
            <p className="mt-1 text-xs text-ink-faint">{text('The report name is the only required concept field.', 'اسم التقرير هو الحقل الوحيد المطلوب للفكرة.')}</p>
          </div>
          {editing && <GlassButton size="sm" onClick={() => {
            setEditing(null);
            setPayload(EMPTY_PAYLOAD);
            setContext(EMPTY_CONTEXT);
            setDefinition(null);
            preview.reset();
          }}>{text('New draft', 'مسودة جديدة')}</GlassButton>}
        </div>
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          <label className="text-xs font-bold">{text('Draft name', 'اسم المسودة')}<GlassInput value={payload.name} onChange={(event) => setPayload({ ...payload, name: event.target.value })} className="mt-1" /></label>
          <label className="text-xs font-bold">{text('Goals', 'الأهداف')}<GlassInput value={(payload.goals ?? []).join(', ')} onChange={(event) => setPayload({ ...payload, goals: event.target.value.split(',').map((value) => value.trim()).filter(Boolean) })} className="mt-1" /></label>
          <label className="text-xs font-bold lg:col-span-2">{text('Concept description', 'وصف الفكرة')}<GlassTextarea rows={3} value={payload.description ?? ''} onChange={(event) => setPayload({ ...payload, description: event.target.value || undefined })} className="mt-1" /></label>
        </div>
        <div className="mt-4"><ContextFields value={context} onChange={setContext} /></div>
        <div className="mt-4 rounded-xl border border-aeblack-100 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><h4 className="text-sm font-extrabold">{text('Structured report definition', 'تعريف التقرير المنظم')}</h4><p className="mt-1 text-xs text-ink-faint">{text('Optional for concept drafts; required before verification.', 'اختياري لمسودات الفكرة ومطلوب قبل التحقق.')}</p></div>
            {definition
              ? <GlassButton size="sm" variant="danger" onClick={() => setDefinition(null)}>{text('Remove definition', 'إزالة التعريف')}</GlassButton>
              : <GlassButton size="sm" onClick={() => setDefinition(blankDefinition())}>{text('Configure report', 'إعداد التقرير')}</GlassButton>}
          </div>
          {definition && <div className="mt-4"><ReportDefinitionBuilder slug={slug} definition={definition} onChange={setDefinition} /></div>}
        </div>
        {editing && <div className="mt-4"><ResourceGuidance slug={slug} draft={editing} workspace={workspace} selectedNoteIds={selectedNoteIds} onApplied={refresh} /></div>}
        <div className="mt-4 flex justify-end"><GlassButton variant="primary" disabled={save.isPending || !payload.name.trim()} onClick={() => save.mutate()}>{text('Save report draft', 'حفظ مسودة التقرير')}</GlassButton></div>
      </GlassCard>
      {definition && (
        <GlassCard className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div><h3 className="text-base font-extrabold">{text('Responsive email preview', 'معاينة البريد المتجاوب')}</h3><p className="mt-1 text-xs text-ink-faint">{text('Sample mode is deterministic. Live-safe mode uses only approved tenant metadata.', 'وضع العينة حتمي. يستخدم الوضع المباشر الآمن بيانات وصفية معتمدة للمستأجر فقط.')}</p></div>
            <div className="flex flex-wrap gap-2">
              <GlassSelect value={dataMode} onChange={(event) => setDataMode(event.target.value as typeof dataMode)}><option value="sample">{text('Sample data', 'بيانات عينة')}</option><option value="live_safe">{text('Live-safe data', 'بيانات مباشرة آمنة')}</option></GlassSelect>
              <GlassButton size="sm" onClick={() => setMobilePreview((value) => !value)}>{mobilePreview ? text('Desktop', 'سطح المكتب') : text('Mobile', 'الهاتف')}</GlassButton>
              <GlassButton variant="primary" disabled={preview.isPending} onClick={() => preview.mutate()}>{preview.isPending ? text('Rendering…', 'جارٍ العرض…') : text('Render preview', 'عرض المعاينة')}</GlassButton>
            </div>
          </div>
          {preview.data && (
            <div className="mt-4 overflow-auto rounded-xl bg-aeblack-50 p-4">
              <iframe
                title={text('Report email preview', 'معاينة بريد التقرير')}
                sandbox=""
                srcDoc={preview.data.preview.rendered.html}
                className="mx-auto h-[760px] rounded-lg border border-aeblack-100 bg-white transition-[width]"
                style={{ width: mobilePreview ? 390 : '100%', maxWidth: 760 }}
              />
              {preview.data.preview.warnings.length > 0 && <p className="mt-2 text-xs text-ink-faint">{preview.data.preview.warnings.join(' ')}</p>}
            </div>
          )}
          <div className="mt-5 border-t border-aeblack-100 pt-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h4 className="text-sm font-extrabold">{text('Send a real test email', 'إرسال بريد اختبار حقيقي')}</h4>
                <p className="mt-1 max-w-2xl text-xs text-ink-faint">
                  {text(
                    'Prepare a short-lived intent, review the masked recipient, then explicitly confirm. Every attempt is rate-limited and audited.',
                    'جهّز طلباً قصير الصلاحية وراجع المستلم المخفي ثم أكد صراحةً. كل محاولة محدودة المعدل ومسجلة.',
                  )}
                </p>
              </div>
              <StatusPill state="in_review" label={text('Real email', 'بريد حقيقي')} />
            </div>
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <label className="min-w-64 flex-1 text-xs font-bold">
                {text('Recipient email', 'بريد المستلم')}
                <GlassInput
                  type="email"
                  value={recipient}
                  onChange={(event) => {
                    setRecipient(event.target.value);
                    setTestIntent(null);
                    setSendConfirmed(false);
                  }}
                  className="mt-1"
                  dir="ltr"
                  placeholder="name@example.com"
                />
              </label>
              <GlassButton
                disabled={prepareTestSend.isPending || !recipient.trim()}
                onClick={() => prepareTestSend.mutate()}
              >
                {prepareTestSend.isPending ? text('Preparing…', 'جارٍ التجهيز…') : text('Prepare test send', 'تجهيز إرسال الاختبار')}
              </GlassButton>
            </div>
            {testIntent && (
              <div className="mt-3 rounded-xl border border-aegold-300 bg-aegold-50 p-4">
                <div className="text-sm font-bold">
                  {text('Confirm delivery to', 'تأكيد التسليم إلى')} <span dir="ltr">{testIntent.maskedRecipient}</span>
                </div>
                <p className="mt-1 text-[10px] text-ink-faint">
                  {text('Expires', 'ينتهي')} {new Date(testIntent.expiresAt).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')}
                </p>
                <label className="mt-3 flex cursor-pointer items-start gap-2 text-xs font-bold text-ink-soft">
                  <input
                    type="checkbox"
                    checked={sendConfirmed}
                    onChange={(event) => setSendConfirmed(event.target.checked)}
                    className="mt-0.5 h-4 w-4"
                  />
                  {text(
                    'I confirm that this action sends a real email to the masked recipient above.',
                    'أؤكد أن هذا الإجراء يرسل بريداً حقيقياً إلى المستلم المخفي أعلاه.',
                  )}
                </label>
                <div className="mt-3 flex justify-end">
                  <GlassButton
                    variant="primary"
                    disabled={!sendConfirmed || confirmTestSend.isPending}
                    onClick={() => confirmTestSend.mutate()}
                  >
                    {confirmTestSend.isPending ? text('Sending…', 'جارٍ الإرسال…') : text('Confirm and send', 'تأكيد وإرسال')}
                  </GlassButton>
                </div>
              </div>
            )}
            {sentMessage && (
              <div className="aegov-alert alert-success mt-3" role="status">
                <div className="alert-content">{text('Provider accepted the real test email.', 'قبل المزود بريد الاختبار الحقيقي.')} <code>{sentMessage}</code></div>
              </div>
            )}
          </div>
        </GlassCard>
      )}
      <DeliveryScheduleForm slug={slug} workspace={workspace} />
    </div>
  );
}
